import {useCallback} from 'react';
import {useAuth} from '../../contexts/AuthContext';
import {useUser} from '../../contexts/UserContext';
import {
  fetchCompanyEncounterList,
  searchCompanyEncountersByText,
} from '../../services/encounterService';
import usePaginatedPlannerEncounterHistory from './usePaginatedPlannerEncounterHistory';

const PAGE_SIZE = 100;

export default function usePlannerCompanyEncounterHistory({
  cacheScope = '',
  companyId,
  facilityId = null,
  searchText = '',
  enabled = true,
}: Record<string, any> = {}) {
  const trimmedSearch = String(searchText ?? '').trim();
  // Session-privacy viewer: private encounters are dropped from these
  // company-wide pages unless the viewer contributed to them (or is a
  // superadmin). Derived here so every consumer of this hook is covered
  // without threading viewer props through the planner tree.
  const {currentUser} = useAuth();
  const {userData} = useUser();
  const viewerId = currentUser?.uid || null;
  const viewerRole = userData?.role || null;

  // With a query, hit the `search_tokens` index directly so matches come from
  // the whole company history rather than just the most-recent page we'd
  // otherwise load and filter client-side. Without one, fetch recent history
  // and bind the facility fallback so a missing `company_id` composite index
  // narrows to the user's own facility instead of erroring the whole panel.
  const fetchPage = useCallback(
    (identity: any, options: any) => {
      const withViewer = {
        ...options,
        viewer: {userId: viewerId, role: viewerRole},
      };
      return trimmedSearch
        ? searchCompanyEncountersByText(identity, trimmedSearch, withViewer)
        : fetchCompanyEncounterList(
            identity,
            facilityId ? {...withViewer, facilityId} : withViewer,
          );
    },
    [facilityId, trimmedSearch, viewerId, viewerRole],
  );

  // Cached pages are post-filter, so the cache key carries the viewer —
  // otherwise switching accounts on one device within the cache TTL could
  // replay a differently-filtered page from the previous user.
  const viewerScope = `${cacheScope}::u:${viewerId || 'anon'}`;

  return usePaginatedPlannerEncounterHistory({
    cacheKey: 'company-encounter-history',
    // Key cached pages on the query so each search keeps its own result set.
    cacheScope: trimmedSearch
      ? `${viewerScope}::q:${trimmedSearch}`
      : viewerScope,
    enabled,
    errorLabel: 'planner company encounter history',
    fetchPage,
    identity: companyId,
    pageSize: PAGE_SIZE,
  });
}
